import React, { useState, useEffect, useCallback } from "react";
import { Row, Col, Divider, Typography, message, Input, InputNumber } from "antd";
import { Formik } from "formik";
import * as Yup from "yup";
import FormInput from "components/FormItems/FormInput";
import FormSelectInput from "components/FormItems/FormSelectInput";
import AppTable from "components/AppTable/AppTable";
import AppButton from "components/AppButton/AppButton";
import { inject, observer } from "mobx-react";
import { fetchCurrencies } from "service/userService";
import { PAYMENT_MODE_OPTIONS, PROVIDER_OPTIONS, SERVICE_TYPE_OPTIONS } from "constant/commonList";
import AlertModal from "components/Modals/AlertModal";
import { validAusNumberRegExpDeluxe } from "constant/messageConstants";

const dedicatedNumberValidationSchema = Yup.object().shape({
  number: Yup.string()
    .matches(validAusNumberRegExpDeluxe, "Please enter a valid phone number")
    .min(9, "Number must be at least 9 digits")
    .required("Number is required"),
  amount: Yup.number().min(0, "Amount must be positive"),
  country: Yup.string().required("Country is required")
});

const businessNameValidationSchema = Yup.object().shape({
  businessName: Yup.string()
    .min(4, "Business Name must be at least 4 characters long")
    .max(11, "Business Name must be at most 11 characters long")
    .required("Enter Business Name")
    .matches(/^[A-Za-z0-9 \-_\.]*[A-Za-z0-9-_\.][A-Za-z0-9 \-_\.]*$/, "Business name can only contain letters, numbers, spaces, hyphens, underscores, and periods")
});

const personalNumberValidationSchema = Yup.object().shape({
  personalNumber: Yup.string()
    .matches(validAusNumberRegExpDeluxe, "Please enter a valid phone number")
    .min(9, "Number must be at least 9 digits")
    .required("Personal number is required")
});

const SenderIdForm = ({ user, readMode = false, userStore, userSenderIds }) => {
  const [countries, setCountries] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [editingIndex, setEditingIndex] = useState(-1);
  const [editingData, setEditingData] = useState({});
  const [confirmModal, setConfirmModal] = useState(false);
  const [conflictUser, setConflictUser] = useState(null);
  const [pendingSubmission, setPendingSubmission] = useState(null);
  const [deleteConfirmModal, setDeleteConfirmModal] = useState(false);
  const [deleteTargetIndex, setDeleteTargetIndex] = useState(null);
  const [deleteTargetNumber, setDeleteTargetNumber] = useState("");
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [editConfirmModal, setEditConfirmModal] = useState(false);
  const [editLoading, setEditLoading] = useState(false);

  const [currentData, setCurrentData] = useState({
    mobileSenders: [],
    businessNames: [],
    personalNumbers: []
  });

  const safeParseArray = useCallback(data => {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    if (typeof data === "string") {
      try {
        const parsed = JSON.parse(data);
        return Array.isArray(parsed) ? parsed : [];
      } catch (e) {
        console.error("Error parsing array data:", e);
        return [];
      }
    }
    return [];
  }, []);

  useEffect(() => {
    const mobileSenders = safeParseArray(userSenderIds?.mobileSenders);
    const businessNames = safeParseArray(userSenderIds?.senders);
    const personalNumbers = safeParseArray(userSenderIds?.personalSenders);

    const data = {
      mobileSenders,
      businessNames,
      personalNumbers
    };
    setCurrentData(data);
  }, [userSenderIds, safeParseArray]);

  useEffect(() => {
    const fetchData = async () => {
      fetchCountries();
    };

    fetchData();
  }, []);

  const fetchCountries = async () => {
    setLoadingCountries(true);
    try {
      const response = await fetchCurrencies();
      const countryOptions = response.data.map(country => ({
        label: country.country,
        value: country._id,
        code: country.code || country.country,
        id: country._id
      }));
      setCountries(countryOptions);
    } catch (error) {
      console.error("Error fetching countries:", error);
    } finally {
      setLoadingCountries(false);
    }
  };

  const startEdit = (record, index) => {
    setEditingIndex(index);
    setEditingData({ ...record });
  };

  const cancelEdit = () => {
    setEditingIndex(-1);
    setEditingData({});
  };

  const saveEdit = async () => {
    const newData = [...currentData.mobileSenders];
    newData[editingIndex] = { ...editingData };

    setCurrentData(prev => ({
      ...prev,
      mobileSenders: newData
    }));

    await userStore.updateMobileUserSenderIds(user.id, editingData);

    message.success("Dedicated number updated successfully");
    cancelEdit();
  };

  const confirmDelete = (index, number) => {
    setDeleteTargetIndex(index);
    setDeleteTargetNumber(number);
    setDeleteConfirmModal(true);
  };

  const handleConfirmDelete = async () => {
    if (deleteTargetIndex === null) return;
    setDeleteLoading(true);
    try {
      await deleteDedicatedNumber(deleteTargetIndex);
    } finally {
      setDeleteLoading(false);
      setDeleteConfirmModal(false);
      setDeleteTargetIndex(null);
      setDeleteTargetNumber("");
    }
  };

  const confirmSaveEdit = () => {
    setEditConfirmModal(true);
  };

  const handleConfirmSaveEdit = async () => {
    setEditLoading(true);
    try {
      await saveEdit();
    } finally {
      setEditLoading(false);
      setEditConfirmModal(false);
    }
  };

  const handleEditChange = (field, value) => {
    setEditingData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const submitDedicatedNumber = async (formattedNumber, values, selectedCountry, resetForm, forceAssign = false) => {
    const newItem = {
      ...values,
      number: formattedNumber,
      country: selectedCountry.id
    };

    const updatedMobileSenders = [...currentData.mobileSenders, newItem];
    const passdata = {
      mobileSenders: updatedMobileSenders,
      senders: currentData.businessNames,
      personalSenders: currentData.personalNumbers,
      type: "add",
      forceAssign
    };

    const res = await userStore.updateUserSenderIds(user.id, passdata);

    if (res.status === false) throw new Error("Sender id already in use");

    setCurrentData(prev => ({
      ...prev,
      mobileSenders: updatedMobileSenders
    }));

    message.success("Dedicated number added successfully");
    resetForm();
  };

  const handleConfirmAssignment = async () => {
    if (!pendingSubmission) return;
    try {
      await submitDedicatedNumber(pendingSubmission.formattedNumber, pendingSubmission.values, pendingSubmission.selectedCountry, pendingSubmission.resetForm, true);
    } catch (error) {
      console.error("Error adding dedicated number:", error);
      message.error("Failed to add dedicated number");
    } finally {
      setConfirmModal(false);
      setPendingSubmission(null);
      setConflictUser(null);
    }
  };

  const handleAddDedicatedNumber = async (values, { setSubmitting, resetForm }) => {
    try {
      const selectedCountry = countries.find(c => c.value === values.country);
      if (!selectedCountry) {
        message.warning("Please select a country");
        return;
      }

      let formattedNumber = values.number.trim();

      if (formattedNumber.startsWith("+")) {
        formattedNumber = formattedNumber.substring(1);
      }

      if (!formattedNumber.startsWith("61")) {
        formattedNumber = "61" + formattedNumber;
      }

      const numberExistsForCurrentUser = currentData.mobileSenders.some(sender => sender.number === formattedNumber);

      if (numberExistsForCurrentUser) {
        message.warning("This dedicated number is already assigned to this user");
        setSubmitting(false);
        return;
      }

      const checkRes = await userStore.checkDedicatedNumber(formattedNumber);

      if (checkRes?.assigned) {
        setConflictUser(checkRes.allAssignedUsers || [checkRes.assignedTo]);
        setPendingSubmission({ formattedNumber, values, selectedCountry, resetForm });
        setSubmitting(false);
        setConfirmModal(true);
        return;
      }

      await submitDedicatedNumber(formattedNumber, values, selectedCountry, resetForm);
    } catch (error) {
      console.error("Error adding dedicated number:", error);
      message.error("Failed to add dedicated number");
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddBusinessName = async (values, { setSubmitting, resetForm }) => {
    try {
      const businessName = values.businessName.trim();

      if (!businessName) {
        message.warning("Business name cannot be empty");
        setSubmitting(false);
        return;
      }

      const businessNameExists = currentData.businessNames.some(name => name.toLowerCase() === businessName.toLowerCase());

      if (businessNameExists) {
        message.warning("Business name already exists");
        setSubmitting(false);
        return;
      }

      const updatedBusinessNames = [...currentData.businessNames, businessName];
      const response = {
        mobileSenders: currentData.mobileSenders,
        senders: updatedBusinessNames,
        personalSenders: currentData.personalNumbers,
        type: "add"
      };

      const res = await userStore.updateUserSenderIds(user.id, response);

      if (res.status === false) throw new Error("Failed to update business name");

      setCurrentData(prev => ({
        ...prev,
        businessNames: updatedBusinessNames
      }));

      message.success("Business name added successfully");
      resetForm();
    } catch (error) {
      console.error("Error adding business name:", error);
      message.error("Failed to add business name");
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddPersonalNumber = async (values, { setSubmitting, resetForm }) => {
    try {
      const personalNumber = values.personalNumber.trim();

      if (!personalNumber) {
        message.warning("Personal number cannot be empty");
        setSubmitting(false);
        return;
      }

      const personalNumberExists = currentData.personalNumbers.some(number => number === personalNumber);

      if (personalNumberExists) {
        message.warning("Personal number already exists");
        setSubmitting(false);
        return;
      }

      const updatedPersonalNumbers = [...currentData.personalNumbers, personalNumber];
      const response = {
        mobileSenders: currentData.mobileSenders,
        senders: currentData.businessNames,
        personalSenders: updatedPersonalNumbers,
        type: "add"
      };

      const res = await userStore.updateUserSenderIds(user.id, response);

      if (res.status === false) throw new Error("Failed to update personal number");

      setCurrentData(prev => ({
        ...prev,
        personalNumbers: updatedPersonalNumbers
      }));

      message.success("Personal number added successfully");
      resetForm();
    } catch (error) {
      console.error("Error adding personal number:", error);
      message.error("Failed to add personal number");
    } finally {
      setSubmitting(false);
    }
  };

  // Delete handlers
  const deleteDedicatedNumber = useCallback(
    async index => {
      const updatedMobileSenders = currentData.mobileSenders.filter((_, i) => i !== index);

      const response = {
        mobileSenders: updatedMobileSenders,
        senders: currentData.businessNames,
        personalSenders: currentData.personalNumbers,
        type: "delete"
      };

      await userStore.updateUserSenderIds(user.id, response);

      setCurrentData(prev => ({
        ...prev,
        mobileSenders: updatedMobileSenders
      }));

      message.success("Dedicated number removed");
    },
    [currentData.mobileSenders, currentData.businessNames, currentData.personalNumbers, user.id, userStore]
  );

  const deleteBusinessName = useCallback(
    async name => {
      const updatedBusinessNames = currentData.businessNames.filter(item => item !== name);

      const response = {
        mobileSenders: currentData.mobileSenders,
        senders: updatedBusinessNames,
        personalSenders: currentData.personalNumbers,
        type: "delete"
      };

      await userStore.updateUserSenderIds(user.id, response);

      setCurrentData(prev => ({
        ...prev,
        businessNames: updatedBusinessNames
      }));

      message.success("Business name removed");
    },
    [currentData.mobileSenders, currentData.businessNames, currentData.personalNumbers, user.id, userStore]
  );

  const deletePersonalNumber = useCallback(
    async index => {
      const updatedPersonalNumbers = currentData.personalNumbers.filter((_, i) => i !== index);

      const response = {
        body: {
          mobileSenders: currentData.mobileSenders,
          senders: currentData.businessNames,
          personalSenders: updatedPersonalNumbers
        },
        type: "delete"
      };

      await userStore.updateUserSenderIds(user.id, response);

      setCurrentData(prev => ({
        ...prev,
        personalNumbers: updatedPersonalNumbers
      }));

      message.success("Personal number removed");
    },
    [currentData.mobileSenders, currentData.businessNames, currentData.personalNumbers, user.id, userStore]
  );

  // Helper function to render country info
  const renderCountryInfo = useCallback(
    country => {
      if (!country) return "N/A";
      if (typeof country === "object") {
        return country.label || country.country || "N/A";
      }
      const foundCountry = countries.find(c => c.value === country || c.id === country);
      return foundCountry?.label || "N/A";
    },
    [countries]
  );

  // Render editable cell
  const renderEditableCell = (value, field, record, index) => {
    if (editingIndex === index) {
      if (field === "amount") {
        return <InputNumber value={editingData[field]} onChange={val => handleEditChange(field, val)} style={{ width: "100%" }} min={0} />;
      } else if (field === "provider") {
        return (
          <select
            value={editingData[field]}
            onChange={e => handleEditChange(field, e.target.value)}
            style={{
              width: "100%",
              padding: "4px 8px",
              border: "1px solid #d9d9d9",
              borderRadius: "6px"
            }}
          >
            {PROVIDER_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );
      } else if (field === "serviceType") {
        return (
          <select
            value={editingData[field]}
            onChange={e => handleEditChange(field, e.target.value)}
            style={{
              width: "100%",
              padding: "4px 8px",
              border: "1px solid #d9d9d9",
              borderRadius: "6px"
            }}
          >
            {SERVICE_TYPE_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );
      } else if (field === "paymentMode") {
        return (
          <select
            value={editingData[field]}
            onChange={e => handleEditChange(field, e.target.value)}
            style={{
              width: "100%",
              padding: "4px 8px",
              border: "1px solid #d9d9d9",
              borderRadius: "6px"
            }}
          >
            {PAYMENT_MODE_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );
      } else if (field === "country") {
        return (
          <select
            value={editingData[field]}
            onChange={e => handleEditChange(field, e.target.value)}
            style={{
              width: "100%",
              padding: "4px 8px",
              border: "1px solid #d9d9d9",
              borderRadius: "6px"
            }}
          >
            {countries.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );
      } else {
        return <Input value={editingData[field]} onChange={e => handleEditChange(field, e.target.value)} />;
      }
    }

    // Display normal value
    if (field === "amount") {
      return `$${value || 0}`;
    } else if (field === "provider") {
      return value?.charAt(0).toUpperCase() + value?.slice(1) || "N/A";
    } else if (field === "serviceType") {
      return value?.toUpperCase() || "N/A";
    } else if (field === "paymentMode") {
      return value?.charAt(0).toUpperCase() + value?.slice(1) || "N/A";
    } else if (field === "country") {
      return renderCountryInfo(value);
    }

    return value || "N/A";
  };

  // Table column definitions
  const getDedicatedNumberColumns = useCallback(() => {
    const columns = [
      {
        title: "Number",
        dataIndex: "number",
        key: "number",
        render: (text, record, index) => renderEditableCell(text, "number", record, index)
      },
      {
        title: "Provider",
        dataIndex: "provider",
        key: "provider",
        render: (text, record, index) => renderEditableCell(text, "provider", record, index)
      },
      {
        title: "Service Type",
        dataIndex: "serviceType",
        key: "serviceType",
        render: (text, record, index) => renderEditableCell(text, "serviceType", record, index)
      },
      {
        title: "Payment Mode",
        dataIndex: "paymentMode",
        key: "paymentMode",
        render: (text, record, index) => renderEditableCell(text, "paymentMode", record, index)
      },
      {
        title: "Type",
        dataIndex: "type",
        key: "type",
        render: (text, record, index) => renderEditableCell(text, "type", record, index)
      },
      {
        title: "Country",
        dataIndex: "country",
        key: "country",
        render: (text, record, index) => renderEditableCell(text, "country", record, index)
      },
      {
        title: "Amount",
        dataIndex: "amount",
        key: "amount",
        render: (text, record, index) => renderEditableCell(text, "amount", record, index)
      }
    ];

    if (!readMode) {
      columns.push({
        title: "Action",
        key: "action",
        render: (text, record, index) => {
          if (editingIndex === index) {
            return (
              <div style={{ display: "flex", gap: "8px" }}>
                <a onClick={confirmSaveEdit} style={{ color: "#52c41a" }}>
                  Save
                </a>
                <a onClick={cancelEdit} style={{ color: "#faad14" }}>
                  Cancel
                </a>
              </div>
            );
          }

          return (
            <div style={{ display: "flex", gap: "8px" }}>
              <i
                className="fas fa-edit"
                style={{
                  cursor: "pointer",
                  color: "#1890ff",
                  opacity: editingIndex !== -1 ? 0.5 : 1,
                  pointerEvents: editingIndex !== -1 ? "none" : "auto"
                }}
                onClick={() => startEdit(record, index)}
                title="Edit"
              />
              <i
                className="fas fa-trash-alt"
                style={{
                  cursor: "pointer",
                  color: "#ff4d4f",
                  opacity: editingIndex !== -1 ? 0.5 : 1,
                  pointerEvents: editingIndex !== -1 ? "none" : "auto"
                }}
                onClick={() => confirmDelete(index, record.number)}
                title="Delete"
              />
            </div>
          );
        }
      });
    }

    return columns;
  }, [readMode, editingIndex, editingData, renderCountryInfo, countries]);

  const getBusinessNameColumns = useCallback(
    () => [
      {
        title: "Business Names",
        dataIndex: "name",
        key: "name",
        render: (text, record, index) => (
          <div className="d-flex align-items-center justify-content-between">
            <span>{text}</span>
            {!readMode && <i className="fas fa-trash-alt" style={{ cursor: "pointer", color: "#ff4d4f" }} onClick={() => deleteBusinessName(text)} />}
          </div>
        )
      }
    ],
    [readMode, deleteBusinessName]
  );

  const getPersonalNumberColumns = useCallback(
    () => [
      {
        title: "Personal Numbers",
        dataIndex: "number",
        key: "number",
        render: (text, record, index) => (
          <div className="d-flex align-items-center justify-content-between">
            <span>{text}</span>
            {!readMode && <i className="fas fa-trash-alt" style={{ cursor: "pointer", color: "#ff4d4f" }} onClick={() => deletePersonalNumber(index)} />}
          </div>
        )
      }
    ],
    [readMode, deletePersonalNumber]
  );

  return (
    <div>
      <Row gutter={20}>
        <Col span={24}>
          <Typography.Title level={4}>Dedicated Numbers</Typography.Title>
        </Col>

        {!readMode && (
          <Col span={24}>
            <Formik
              initialValues={{
                number: "",
                provider: "",
                serviceType: "",
                paymentMode: "",
                country: "",
                amount: 15
              }}
              validationSchema={dedicatedNumberValidationSchema}
              onSubmit={handleAddDedicatedNumber}
            >
              {({ values, errors, touched, handleChange, handleSubmit, isSubmitting, setFieldValue }) => (
                <>
                  <Row gutter={[20, 20]} type="flex" justify="center">
                    <Col span={12}>
                      <FormInput
                        containerClassname="mb-0 "
                        placeholder="e.g. 614XXXXXXX"
                        error={errors.number}
                        touched={touched.number}
                        label="Dedicated Number"
                        value={values.number}
                        onChange={handleChange}
                        name="number"
                        size="default"
                      />
                    </Col>

                    <Col span={12}>
                      <FormSelectInput
                        containerClassname="mb-0"
                        label="Provider"
                        name="provider"
                        placeholder="Select Provider"
                        options={PROVIDER_OPTIONS}
                        onChange={value => setFieldValue("provider", value)}
                        value={values.provider}
                        size="default"
                      />
                    </Col>
                  </Row>
                  <Row gutter={[20, 20]} type="flex" justify="center">
                    <Col span={12}>
                      <FormSelectInput
                        containerClassname="mb-0"
                        label="Service Type"
                        name="serviceType"
                        placeholder="Select Service Type"
                        options={SERVICE_TYPE_OPTIONS}
                        onChange={value => setFieldValue("serviceType", value)}
                        value={values.serviceType}
                        size="default"
                      />
                    </Col>
                    <Col span={12}>
                      <FormSelectInput
                        containerClassname="mb-0"
                        label="Payment Mode"
                        name="paymentMode"
                        placeholder="Select Payment Mode"
                        options={PAYMENT_MODE_OPTIONS}
                        onChange={value => setFieldValue("paymentMode", value)}
                        value={values.paymentMode}
                        size="default"
                      />
                    </Col>
                    <Col span={12}>
                      <FormSelectInput
                        containerClassname="mb-0"
                        label="Country"
                        name="country"
                        placeholder="Select Country"
                        options={countries}
                        onChange={value => setFieldValue("country", value)}
                        value={values.country}
                        size="default"
                        loading={loadingCountries}
                        error={errors.country}
                        touched={touched.country}
                      />
                    </Col>
                    <Col span={12}>
                      <FormInput
                        containerClassname="mb-0"
                        placeholder="0.00"
                        error={errors.amount}
                        touched={touched.amount}
                        label="Amount ($)"
                        value={values.amount}
                        onChange={handleChange}
                        name="amount"
                        size="default"
                        type="number"
                      />
                    </Col>
                    <Col span={24} style={{ paddingTop: "4px", paddingBottom: "7px" }}>
                      <AppButton
                        label="Add Dedicated Number"
                        light
                        type="button"
                        prefixIcon={<i className="fas fa-plus me-2" />}
                        onClick={handleSubmit}
                        isSubmitting={isSubmitting}
                        disabled={isSubmitting}
                      />
                    </Col>
                  </Row>
                </>
              )}
            </Formik>
          </Col>
        )}

        <Col span={24} className="mt-3">
          {currentData.mobileSenders.length > 0 && (
            <AppTable columns={getDedicatedNumberColumns()} dataSource={currentData.mobileSenders} pagination={false} bordered rowKey={(record, index) => record._id || index} />
          )}
        </Col>
      </Row>

      <Divider />

      <Row gutter={20}>
        <Col span={24}>
          <Typography.Title level={4}>Business Names</Typography.Title>
        </Col>

        {!readMode && (
          <Col span={24}>
            <Formik
              initialValues={{
                businessName: ""
              }}
              validationSchema={businessNameValidationSchema}
              onSubmit={handleAddBusinessName}
            >
              {({ values, errors, touched, handleChange, handleSubmit, isSubmitting }) => (
                <Row gutter={20}>
                  <Col span={18}>
                    <FormInput
                      containerClassname="mb-0"
                      placeholder="Add Business Name e.g. SMSBump"
                      error={errors.businessName}
                      touched={touched.businessName}
                      label="Business Name"
                      value={values.businessName}
                      onChange={handleChange}
                      name="businessName"
                      size="default"
                    />
                  </Col>
                  <Col span={6}>
                    <div style={{ marginTop: "52px" }}>
                      <AppButton label="Add" type="button" prefixIcon={<i className="fas fa-plus me-2" />} onClick={handleSubmit} light isSubmitting={isSubmitting} disabled={isSubmitting} />
                    </div>
                  </Col>
                </Row>
              )}
            </Formik>
          </Col>
        )}

        <Col span={24}>
          {currentData.businessNames.length > 0 && (
            <AppTable
              columns={getBusinessNameColumns()}
              dataSource={currentData.businessNames.map((name, index) => ({
                name,
                key: index
              }))}
              pagination={false}
              bordered
              rowKey="key"
              light
            />
          )}
        </Col>
      </Row>

      <Divider />

      <Row gutter={20}>
        <Col span={24}>
          <Typography.Title level={4}>Personal Numbers</Typography.Title>
        </Col>

        {!readMode && (
          <Col span={24}>
            <Formik
              initialValues={{
                personalNumber: ""
              }}
              validationSchema={personalNumberValidationSchema}
              onSubmit={handleAddPersonalNumber}
            >
              {({ values, errors, touched, handleChange, handleSubmit, isSubmitting }) => (
                <Row gutter={20}>
                  <Col span={18}>
                    <FormInput
                      containerClassname="mb-0"
                      placeholder="Add Personal Number e.g. 614xxxxxxxx"
                      error={errors.personalNumber}
                      touched={touched.personalNumber}
                      label="Personal Number"
                      value={values.personalNumber}
                      onChange={handleChange}
                      name="personalNumber"
                      size="default"
                    />
                  </Col>
                  <Col span={6}>
                    <div style={{ marginTop: "52px" }}>
                      <AppButton label="Add" type="button" prefixIcon={<i className="fas fa-plus me-2" />} onClick={handleSubmit} light isSubmitting={isSubmitting} disabled={isSubmitting} />
                    </div>
                  </Col>
                </Row>
              )}
            </Formik>
          </Col>
        )}

        <Col span={24}>
          {currentData.personalNumbers.length > 0 && (
            <AppTable
              columns={getPersonalNumberColumns()}
              dataSource={currentData.personalNumbers.map((number, index) => ({
                number,
                key: index
              }))}
              pagination={false}
              bordered
              rowKey="key"
            />
          )}
        </Col>
      </Row>

      <AlertModal
        visible={confirmModal}
        warning
        title="Number Already Assigned"
        modalWidth={520}
        descriptionNode={
          <div style={{ width: "100%" }}>
            <p style={{ marginBottom: 8 }}>This number is already assigned to:</p>
            <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 12, fontSize: 13 }}>
              <thead>
                <tr style={{ backgroundColor: "#f5f5f5" }}>
                  <th style={{ border: "1px solid #e8e8e8", padding: "6px 10px", textAlign: "left" }}>#</th>
                  <th style={{ border: "1px solid #e8e8e8", padding: "6px 10px", textAlign: "left" }}>Name</th>
                  <th style={{ border: "1px solid #e8e8e8", padding: "6px 10px", textAlign: "left" }}>Email</th>
                </tr>
              </thead>
              <tbody>
                {(conflictUser || []).map((u, idx) => (
                  <tr key={u.id}>
                    <td style={{ border: "1px solid #e8e8e8", padding: "6px 10px" }}>{idx + 1}</td>
                    <td style={{ border: "1px solid #e8e8e8", padding: "6px 10px", fontWeight: 600 }}>{u.name}</td>
                    <td style={{ border: "1px solid #e8e8e8", padding: "6px 10px" }}>{u.email}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p style={{ marginBottom: 0 }}>Do you want to assign it to this user as well?</p>
          </div>
        }
        primaryBtnProps={{
          label: "Yes",
          onClick: handleConfirmAssignment
        }}
        secondaryBtnProps={{
          label: "Cancel",
          onClick: () => {
            setConfirmModal(false);
            setPendingSubmission(null);
            setConflictUser(null);
          }
        }}
        closable
        onCancel={() => {
          setConfirmModal(false);
          setPendingSubmission(null);
          setConflictUser(null);
        }}
      />

      <AlertModal
        visible={deleteConfirmModal}
        error
        title="Delete Dedicated Number"
        description={`Are you sure you want to delete <strong>${deleteTargetNumber}</strong>?`}
        primaryBtnProps={{
          label: "Delete",
          onClick: handleConfirmDelete
        }}
        secondaryBtnProps={{
          label: "Cancel",
          onClick: () => {
            setDeleteConfirmModal(false);
            setDeleteTargetIndex(null);
            setDeleteTargetNumber("");
          }
        }}
        loading={deleteLoading}
        closable
        onCancel={() => {
          setDeleteConfirmModal(false);
          setDeleteTargetIndex(null);
          setDeleteTargetNumber("");
        }}
      />

      <AlertModal
        visible={editConfirmModal}
        warning
        title="Save Changes"
        description="Are you sure you want to save the changes to this dedicated number?"
        primaryBtnProps={{
          label: "Save",
          onClick: handleConfirmSaveEdit
        }}
        secondaryBtnProps={{
          label: "Cancel",
          onClick: () => setEditConfirmModal(false)
        }}
        loading={editLoading}
        closable
        onCancel={() => setEditConfirmModal(false)}
      />
    </div>
  );
};

export default inject(stores => ({
  userStore: stores.store.userStore
}))(observer(SenderIdForm));
